import { useState, useEffect } from 'react';
import { ORGANIZATION_TYPES } from '../constants';

export const useOrganizationTypeEdit = (initialType = '', initialDivisionSections = null) => {
    const [selectedGrades, setSelectedGrades] = useState([]);
    const [gradeNames, setGradeNames] = useState({});
    const [gradeClasses, setGradeClasses] = useState({});
    const [organizationType, setOrganizationType] = useState(initialType);

    // 기관유형에 따른 기본 상태 설정 함수
    const initializeOrganizationDefaults = (organizationTypeValue) => {
        const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationTypeValue);
        if (!orgType) {
            setSelectedGrades([]);
            setGradeClasses({});
            setGradeNames({});
            return;
        }

        // 기본 학년 설정
        let defaultGrades = [];
        let defaultGradeClasses = {};
        let defaultGradeNames = {};

        if (orgType.grades && orgType.grades.length > 1) {
            // 유치원, 초등학교, 중학교, 고등학교
            defaultGrades = orgType.grades.map((grade, index) => index + 1);

            // 각 학년별 기본 반 설정
            defaultGrades.forEach(grade => {
                const classCount = orgType.classCount || 5;
                const defaultClasses = Array.from({ length: classCount }, (_, i) => '');
                defaultGradeClasses[grade] = {
                    count: classCount,
                    classes: defaultClasses
                };

                // 학년 이름 설정                
                defaultGradeNames[grade] = orgType.grades[grade - 1];
            });
        } else if (orgType.grades && orgType.grades.length === 1) {
            // 청소년 시설, 복지시설, 기타
            defaultGrades = [1];
            defaultGradeClasses[1] = {
                count: 1,
                classes: ['']
            };
            defaultGradeNames[1] = '';
        }

        setSelectedGrades(defaultGrades);
        setGradeClasses(defaultGradeClasses);
        setGradeNames(defaultGradeNames);
    };

    // initialType이 변경될 때 organizationType 업데이트
    useEffect(() => {
        if (initialType !== undefined && initialType !== null && initialType !== organizationType) {
            setOrganizationType(initialType);
        }
    }, [initialType]);

    // 초기 divisionSections에서 상태 복원
    useEffect(() => {
        if (initialDivisionSections) {
            try {
                const divisions = typeof initialDivisionSections === 'string'
                    ? JSON.parse(initialDivisionSections)
                    : initialDivisionSections;

                const grades = Object.keys(divisions);
                const gradeNamesObj = {};
                const gradeClassesObj = {};

                grades.forEach((gradeName, index) => {
                    const gradeNumber = index + 1;
                    gradeNamesObj[gradeNumber] = gradeName;
                    const classes = Array.isArray(divisions[gradeName]) ? divisions[gradeName] : [];
                    gradeClassesObj[gradeNumber] = {
                        count: classes.length,
                        classes: classes
                    };
                });

                setSelectedGrades(grades.map((_, index) => index + 1));
                setGradeNames(gradeNamesObj);
                setGradeClasses(gradeClassesObj);
            } catch (error) {
                console.error('Failed to parse divisionSections:', error);
            }
        } else if (initialType !== undefined && initialType !== null && initialType !== '') {
            initializeOrganizationDefaults(initialType);
        }
    }, [initialDivisionSections, initialType]);

    // 기관유형 변경 핸들러
    const handleOrganizationTypeChange = (option) => {
        const newType = typeof option === 'object' ? option.value : option;
        setOrganizationType(newType);

        // 기관유형이 변경되면 기본 상태로 초기화
        if (newType === '' || newType === null || newType === undefined) {
            setSelectedGrades([]);
            setGradeClasses({});
            setGradeNames({});
        } else {
            // 선택된 기관유형에 따른 기본 상태 설정
            initializeOrganizationDefaults(newType);
        }
    };

    // 학년 개수 조정 핸들러
    const adjustGradeCount = (delta) => {
        setSelectedGrades(prev => {
            const currentCount = prev.length;
            const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
            const minCount = (orgType && orgType.grades && orgType.grades.length === 1) ? 0 : 1;
            const newCount = Math.max(minCount, Math.min(10, currentCount + delta));

            let newGrades;
            if (newCount === 0) {
                newGrades = [];
            } else if (newCount > currentCount) {
                newGrades = [...prev];
                for (let i = currentCount; i < newCount; i++) {
                    newGrades.push(i + 1);
                }
            } else if (newCount < currentCount) {
                newGrades = prev.slice(0, newCount);
            } else {
                newGrades = prev;
            }

            if (newGrades.length > 0) {
                setTimeout(() => {
                    setGradeNames(prevGradeNames => {
                        const newGradeNames = { ...prevGradeNames };
                        const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);

                        if (orgType && orgType.grades) {
                            if (orgType.grades.length === 1) {
                                newGrades.forEach((gradeNumber) => {
                                    if (!newGradeNames[gradeNumber]) {
                                        newGradeNames[gradeNumber] = '';
                                    }
                                });
                            } else {
                                newGrades.forEach((gradeNumber, index) => {
                                    if (index < orgType.grades.length) {
                                        const gradeLabel = orgType.grades[gradeNumber - 1];
                                        if (gradeLabel && !newGradeNames[gradeNumber]) {
                                            newGradeNames[gradeNumber] = gradeLabel;
                                        }
                                    } else {
                                        if (!newGradeNames[gradeNumber]) {
                                            newGradeNames[gradeNumber] = '';
                                        }
                                    }
                                });
                            }
                        }
                        return newGradeNames;
                    });

                    setGradeClasses(prevGradeClasses => {
                        const newGradeClasses = { ...prevGradeClasses };
                        const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);

                        if (orgType) {
                            const classCount = orgType.classCount || 5;

                            newGrades.forEach((gradeNumber) => {
                                if (!newGradeClasses[gradeNumber]) {
                                    const defaultClasses = Array.from({ length: classCount }, (_, i) => '');
                                    newGradeClasses[gradeNumber] = {
                                        count: classCount,
                                        classes: defaultClasses
                                    };
                                }
                            });
                        }
                        return newGradeClasses;
                    });
                }, 0);
            } else {
                setGradeNames({});
                setGradeClasses({});
            }

            return newGrades;
        });
    };

    // 학년 이름 변경 핸들러
    const handleGradeNameChange = (gradeNumber, value) => {
        setGradeNames(prev => ({
            ...prev,
            [gradeNumber]: value
        }));
    };

    // 반 개수 조정 핸들러
    const adjustClassCount = (grade, delta) => {
        setGradeClasses(prev => {
            const currentCount = prev[grade].count;
            const newCount = Math.max(1, Math.min(10, currentCount + delta));
            const currentClasses = prev[grade].classes || [];

            let finalClasses;
            if (delta > 0) {
                finalClasses = [...currentClasses, ...Array.from({ length: delta }, () => '')];
            } else {
                finalClasses = currentClasses.slice(0, newCount);
            }

            return {
                ...prev,
                [grade]: {
                    count: newCount,
                    classes: finalClasses
                }
            };
        });
    };

    // 반 이름 변경 핸들러
    const handleClassNameChange = (grade, classIndex, value) => {
        setGradeClasses(prev => ({
            ...prev,
            [grade]: {
                ...prev[grade],
                classes: prev[grade].classes.map((className, index) =>
                    index === classIndex ? value : className
                )
            }
        }));
    };

    // divisionSections 생성
    const getDivisionSections = () => {
        const newDivisionSections = {};

        selectedGrades.forEach(grade => {
            const gradeName = gradeNames[grade];
            if (gradeName && gradeName.trim() !== '') {
                const classes = gradeClasses[grade]?.classes || [];
                const classNames = classes
                    .filter(className => className && className.trim() !== '')
                    .map(className => className.trim());

                const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
                if (orgType && orgType.grades && orgType.grades.length === 1) {
                    newDivisionSections[gradeName.trim()] = [];
                } else {
                    newDivisionSections[gradeName.trim()] = classNames;
                }
            }
        });

        return newDivisionSections;
    };

    // 유효성 검사
    const isValid = () => {
        if (!organizationType) return false;

        const orgType = ORGANIZATION_TYPES.find(type => type.value === organizationType);
        if (!orgType) return false;

        // 학년 개수가 0이면 유효성 검사 통과
        if (selectedGrades.length === 0) {
            return true;
        }

        // 세부소속1 검사 (학년 이름이 모두 입력되었는지)
        const hasAllGradeNames = selectedGrades.every(grade => {
            const gradeName = gradeNames[grade];
            return gradeName && gradeName.trim() !== '';
        });

        // 세부소속2 검사 (반 이름이 모두 입력되었는지)
        let hasAllClassNames = true;
        if (orgType.grades && orgType.grades.length > 1) {
            hasAllClassNames = selectedGrades.every(grade => {
                const classes = gradeClasses[grade]?.classes || [];
                return classes.every(className => className && className.trim() !== '');
            });
        }

        return hasAllGradeNames && hasAllClassNames;
    };

    return {
        organizationType,
        selectedGrades,
        gradeNames,
        gradeClasses,
        handleOrganizationTypeChange,
        adjustGradeCount,
        handleGradeNameChange,
        adjustClassCount,
        handleClassNameChange,
        getDivisionSections,
        isValid,
        initializeOrganizationDefaults
    };
};

